import { AdjustmentCategory, AdjustmentFrequency, AwsAdjustment, OnPremAdjustment } from '@amzn/awscat-portfolio-assessment-types';
import { SelectProps } from '@amzn/awsui-components-react';

import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals } from '../../../common/LocalizationHelper';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';
import { capitalizeWord } from '../../portfolio-data-asset/utils';
import { calculateTotalCost, calculateYearlyCost, getTitleToDisplay } from './adjustments-helper';
import { AdjustmentItem, AdjustmentType, AwsOnPremAdjustment, SelectOption } from './interfaces';

export const getAdjustmentColumns = (
    currency: string, tcoDuration: number, displayLinkToEditPage: (text: string, id: string) => JSX.Element
): ColumnDefinition<AdjustmentItem>[] => [
    {
        id: 'type',
        header: constants.TYPE,
        data: item => item.type,
        cell: item => item.type,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'title',
        header: constants.TITLE,
        data: item => getTitleToDisplay(item),
        cell: item => displayLinkToEditPage(getTitleToDisplay(item), item.adjustmentId),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'category',
        header: constants.CATEGORY,
        data: item => costCategoryMap.get(item.category)?.label ?? item.category,
        cell: item => costCategoryMap.get(item.category)?.label ?? item.category,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'description',
        header: constants.DESCRIPTION,
        data: item => item.description,
        cell: item => item.description,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'amount',
        header: constants.AMOUNT,
        data: item => roundToInteger(item.amount),
        cell: item => getCell(formatCurrencyNoDecimals(item.amount, currency)),
        type: ColumnTypes.NUMBER,
    },
    {
        id: 'frequency',
        header: constants.FREQUENCY,
        data: item => capitalizeWord(item.frequency),
        cell: item => capitalizeWord(item.frequency),
        type: ColumnTypes.TEXT,
    },
    {
        id: 'yearlyCost',
        header: constants.YEARLY_COST,
        data: item => roundToInteger(calculateYearlyCost(item.amount, item.frequency, tcoDuration)),
        cell: item => getCell(formatCurrencyNoDecimals(calculateYearlyCost(item.amount, item.frequency, tcoDuration), currency)),
        type: ColumnTypes.NUMBER
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(calculateTotalCost(item.amount, item.frequency, tcoDuration)),
        cell: item => getCell(formatCurrencyNoDecimals(calculateTotalCost(item.amount, item.frequency, tcoDuration), currency)),
        type: ColumnTypes.NUMBER
    },
    {
        id: 'awsSupport',
        header: constants.AWS_SUPPORT,
        data: item => item.type === AdjustmentType.AWS ? (item.awsSupport ? constants.TRUE : constants.FALSE) : '-',
        cell: item => item.type === AdjustmentType.AWS ? (item.awsSupport ? constants.TRUE : constants.FALSE) : '-',
        type: ColumnTypes.BOOLEAN,
    },
    {
        id: 'inScope',
        header: constants.IN_SCOPE,
        data: item => item.inScope ? constants.TRUE : constants.FALSE,
        cell: item => item.inScope ? constants.TRUE : constants.FALSE,
        type: ColumnTypes.BOOLEAN,
    },
];

export enum ActionOptions {
    EDIT = 'edit',
    DELETE = 'delete',
}

interface ServiceNameAndCategory {
    name: string;
    category: AdjustmentCategory;
}

export const awsServicesMap: Map<string, ServiceNameAndCategory> = new Map<string, ServiceNameAndCategory>([
    ['ec2', {name: 'Amazon EC2', category: AdjustmentCategory.COMPUTE}], 
    ['ecs', {name: 'Amazon Elastic Container Service (ECS)', category: AdjustmentCategory.COMPUTE}],
    ['eks', {name: 'Amazon Elastic Kubernetes Service (EKS)', category: AdjustmentCategory.COMPUTE}],
    ['lightsail', {name: 'Amazon Lightsail', category: AdjustmentCategory.COMPUTE}],
    ['appRunner', {name: 'AWS App Runner', category: AdjustmentCategory.COMPUTE}],
    ['computeOptimizer', {name: 'AWS Compute Optimizer', category: AdjustmentCategory.COMPUTE}],
    ['fargate', {name: 'AWS Fargate', category: AdjustmentCategory.COMPUTE}],
    ['lambda', {name: 'AWS Lambda', category: AdjustmentCategory.COMPUTE}],
    ['outputs', {name: 'AWS Outposts', category: AdjustmentCategory.COMPUTE}],
    ['vmwareCloud', {name: 'VMware Cloud on AWS', category: AdjustmentCategory.COMPUTE}],
    ['ebs', {name: 'Amazon Elastic Block Store (EBS)', category: AdjustmentCategory.STORAGE}],
    ['efs', {name: 'Amazon Elastic File System (EFS)', category: AdjustmentCategory.STORAGE}],
    ['fsx', {name: 'Amazon FSx', category: AdjustmentCategory.STORAGE}],
    ['glacier', {name: 'Amazon S3 Glacier', category: AdjustmentCategory.STORAGE}],
    ['s3', {name: 'Amazon Simple Storage System (S3)', category: AdjustmentCategory.STORAGE}],
    ['backup', {name: 'AWS Backup', category: AdjustmentCategory.STORAGE}],
    ['snowFamily', {name: 'AWS Snow Family', category: AdjustmentCategory.STORAGE}],
    ['storageGateway', {name: 'AWS Storage Gateway', category: AdjustmentCategory.STORAGE}],
    ['disasterRecovery', {name: 'CloudEndure Disaster Recovery', category: AdjustmentCategory.STORAGE}],
    ['aurora', {name: 'Amazon Aurora', category: AdjustmentCategory.DATABASE}],
    ['documentdb', {name: 'Amazon DocumentDB', category: AdjustmentCategory.DATABASE}],
    ['dynamodb', {name: 'Amazon DynamoDB', category: AdjustmentCategory.DATABASE}],
    ['elastiCache', {name: 'Amazon ElastiCache', category: AdjustmentCategory.DATABASE}],
    ['keyspacesApache', {name: 'Amazon Keyspaces (for Apache Cassandra)', category: AdjustmentCategory.DATABASE}],
    ['memoryDBRedis', {name: 'Amazon MemoryDB for Redis', category: AdjustmentCategory.DATABASE}],
    ['neptune', {name: 'Amazon Neptune', category: AdjustmentCategory.DATABASE}],
    ['rds', {name: 'Amazon RDS', category: AdjustmentCategory.DATABASE}],
    ['rdsVmware', {name: 'Amazon RDS on VMware', category: AdjustmentCategory.DATABASE}],
    ['redshift', {name: 'Amazon Redshift', category: AdjustmentCategory.DATABASE}],
    ['timestream', {name: 'Amazon Timestream', category: AdjustmentCategory.DATABASE}],
    ['apiGateway', {name: 'Amazon API Gateway', category: AdjustmentCategory.NETWORK}],
    ['cloudFront', {name: 'Amazon CloudFront', category: AdjustmentCategory.NETWORK}],
    ['route53', {name: 'Amazon Route 53', category: AdjustmentCategory.NETWORK}],
    ['cloupMap', {name: 'AWS Cloud Map', category: AdjustmentCategory.NETWORK}],
    ['cloudWAN', {name: 'AWS Cloud WAN (Preview)', category: AdjustmentCategory.NETWORK}],
    ['directConnect', {name: 'AWS Direct Connect', category: AdjustmentCategory.NETWORK}],
    ['globalAccelerator', {name: 'AWS Global Accelerator', category: AdjustmentCategory.NETWORK}],
    ['private5g', {name: 'AWS Private 5G', category: AdjustmentCategory.NETWORK}],
    ['privateLink', {name: 'AWS PrivateLink', category: AdjustmentCategory.NETWORK}],
    ['transitGateway', {name: 'AWS Transit Gateway', category: AdjustmentCategory.NETWORK}],
    ['vpn', {name: 'AWS VPN', category: AdjustmentCategory.NETWORK}],
    ['elb', {name: 'Elastic Load Balancing (ELB)', category: AdjustmentCategory.NETWORK}],
    ['ecr', {name: 'Amazon Elastic Container Registry (ECR)', category: AdjustmentCategory.COMPUTE}],
    ['redHatOpenShift', {name: 'Red Hat OpenShift Service on AWS', category: AdjustmentCategory.COMPUTE}],
    ['appStream2-0', {name: 'Amazon AppStream 2.0', category: AdjustmentCategory.COMPUTE}],
    ['workspaces', {name: 'Amazon Workspaces', category: AdjustmentCategory.COMPUTE}],
    ['awsSupport', {name: 'AWS Support', category: AdjustmentCategory.SUPPORT}],
    ['cloudWatch', {name: 'AWS CloudWatch',	category: AdjustmentCategory.OTHER}],
    ['drs', {name: 'AWS Elastic Disaster Recovery (DRS)', category: AdjustmentCategory.OTHER}],
    ['transferFamily', {name: 'AWS Transfer Family', category: AdjustmentCategory.NETWORK}],
    ['dataSync', {name: 'AWS DataSync', category: AdjustmentCategory.NETWORK}],
    ['networkFirewall', {name: 'AWS Network Firewall', category: AdjustmentCategory.NETWORK}],
    ['waf', {name: 'AWS Web Application Firewall (WAF)', category: AdjustmentCategory.NETWORK}],
    ['interRegionDataTransferOut', {name: 'Inter-Region Data Transfer Out', category: AdjustmentCategory.NETWORK}],
    ['dataTransferOut', {name: 'Data Transfer Out', category: AdjustmentCategory.NETWORK}],
    ['dataTransferIn', {name: 'Data Transfer In', category: AdjustmentCategory.NETWORK}],
    ['vpcPeeringDataTransfer', {name: 'VPC Peering Data Transfer', category: AdjustmentCategory.NETWORK}],
    ['intraRegionDataTransfer', {name: 'Intra-Region Data Transfer', category: AdjustmentCategory.NETWORK}],
    ['publicIPDataTransfer', {name: 'Public IP/Elastic IP Data Transfer', category: AdjustmentCategory.NETWORK}],
    ['crossRegionReplication', {name: 'Cross Region Replication', category: AdjustmentCategory.NETWORK}],
]);

export const getAwsServicesOptions = (): SelectOption[] => {
    const options: {label: string, value: string}[] = [];
    awsServicesMap.forEach((value: ServiceNameAndCategory, key: string) => {
        options.push( {
            label: value.name,
            value: key
        });
    });
    return options;
};

interface CategoryDetail {
    label: string;
    isAWSOnly: boolean;
}

const costCategoryMap: Map<AdjustmentCategory, CategoryDetail> = new Map<AdjustmentCategory, CategoryDetail>([
    [AdjustmentCategory.COMPUTE, {label: constants.COMPUTE, isAWSOnly: false}],
    [AdjustmentCategory.DATABASE, {label: constants.DATABASE, isAWSOnly: false}],
    [AdjustmentCategory.NETWORK, {label: constants.NETWORK_UPPERCASE, isAWSOnly: false}],
    [AdjustmentCategory.STORAGE, {label: constants.STORAGE_CAPITALIZED, isAWSOnly: false}],
    [AdjustmentCategory.VM_WARE_CLOUD, {label: constants.VMWARE_CLOUD, isAWSOnly: true}],
    [AdjustmentCategory.SUPPORT, {label: constants.SUPPORT, isAWSOnly: true}],
    [AdjustmentCategory.IT_LABOR, {label: constants.IT_LABOR, isAWSOnly: false}],
    [AdjustmentCategory.MANAGED_SERVICES, {label: constants.MANAGED_SERVICES, isAWSOnly: false}],
    [AdjustmentCategory.APPLICATION_SOFTWARE, {label: constants.APPLICATION_SOFTWARE, isAWSOnly: false}],
    [AdjustmentCategory.OTHER, {label: constants.OTHER, isAWSOnly: false}],
]);

export const getCostCategoryOptions = (type: AdjustmentType): SelectProps.Option[] => {
    const options: SelectProps.Option[] =[];

    costCategoryMap.forEach((categoryDetail: CategoryDetail, key: string) => {
        if (type === AdjustmentType.ON_PREMISES && categoryDetail.isAWSOnly) {
            return;
        }
        options.push({
            label: categoryDetail.label,
            value: key,
        });
    });

    return options;
};

export const frequencyOptions: SelectProps.Option[] = Object.values(AdjustmentFrequency).map(frequency => {
    return {
        label: capitalizeWord(frequency),
        value: frequency,
    };
});

export const getDefaultNewAdjustment = (type: AdjustmentType): AwsOnPremAdjustment => {
    if (type === AdjustmentType.AWS) {
        const item: AwsAdjustment = {
            title: 'ec2', // defaults to aws service
            description: '',
            category: AdjustmentCategory.COMPUTE,
            amount: 0,
            frequency: AdjustmentFrequency.MONTHLY,
            inScope: true,
            awsSupport: true,
        };
        return item;
    } else {
        const item: OnPremAdjustment = {
            title: '',
            description: '',
            category: AdjustmentCategory.COMPUTE,
            amount: 0,
            frequency: AdjustmentFrequency.MONTHLY,
            inScope: true,
        };
        return item;
    }
};
